<template>
  <BubbleHeader :bubble="bubble" />
  <div class="p-6">
    <!-- Author info -->
    <div v-if="bubble.authors && bubble.authors.length" class="mb-2 text-sm text-gray-500">
      By {{ bubble.authors.join(', ') }}
    </div>

    <!-- Main content: Cover photo and Gallery -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
      <!-- Cover photo section -->
      <div
        v-if="bubble.offlineMediaResources?.cover_photo"
        :class="{ 'lg:col-span-2': !galleryImages.length }"
      >
        <div class="relative w-full">
          <NuxtImg
            :src="dataStore + bubble.offlineMediaResources.cover_photo"
            alt="Cover photo"
            class="w-full rounded-lg shadow-md"
            fit="cover"
            :width="galleryImages.length ? 800 : 1600"
            :height="galleryImages.length ? 600 : 900"
          />
        </div>
      </div>

      <!-- Image Gallery -->
      <div v-if="galleryImages.length > 0">
        <h3 class="text-lg font-semibold mb-4">Gallery</h3>
        <MediaGallery :items="galleryImages" :bubble-id="bubble.uuid" />
      </div>
    </div>

    <!-- URL section at bottom -->
    <div
      v-if="bubble.url"
      class="flex items-center justify-between bg-gray-100 rounded-lg p-4 px-4 hover:bg-gray-200 transition-colors duration-300"
    >
      <a
        :href="bubble.url"
        target="_blank"
        rel="noopener noreferrer"
        class="text-blue-600 hover:text-blue-800 font-medium flex items-center"
      >
        <Icon name="heroicons:globe-alt" class="w-5 h-5 mr-3" />
        <span class="truncate">{{ bubble.url }}</span>
      </a>
      <Button
        variant="outline"
        size="sm"
        class="ml-3 whitespace-nowrap"
        @click="copyUrlToClipboard"
      >
        <Icon name="heroicons:clipboard" class="w-4 h-4 mr-2" />
        Copy URL
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { WebPageBubble } from '@/stores/interfaces'
import BubbleHeader from '../BubbleHeader.vue'
import { useRuntimeConfig } from '#app'
import { computed, onMounted, onUnmounted } from 'vue'
import { Button } from '@/components/ui/button'
import MediaGallery from '@/components/ui/media-gallery/MediaGallery.vue'
import type { MediaItem } from '@/components/ui/media-gallery/MediaGallery.vue'
import { useTypeNavigation } from '@/composables/useTypeNavigation'
import { useNavigationStore } from '@/stores/navigationStore'
import { useRouter } from 'vue-router'
import { useLogger } from '@/composables/useLogger'
import { useKeyboardNavigation } from '@/composables/useKeyboardNavigation'

const {
  public: { dataStore },
} = useRuntimeConfig()

const props = defineProps<{
  bubble: WebPageBubble
}>()

const navigationStore = useNavigationStore()
const router = useRouter()
const logger = useLogger()
const { isKeyboardNavigationDisabled } = useKeyboardNavigation()

defineEmits<{
  back: []
}>()

// Use the type navigation composable
useTypeNavigation(props.bubble)

// Function to get base filename without size suffix and extension
const getBaseFilename = (path: string) => {
  const filename = path.split('/').pop() || ''
  // Remove size suffix like _940x731 and extension
  return filename.replace(/(_\d+x\d+)?\.[^.]+$/, '')
}

// Function to get image dimensions from filename
const getDimensions = (path: string) => {
  const match = path.match(/_(\d+)x(\d+)/)
  if (match && match[1] && match[2]) {
    return {
      width: parseInt(match[1], 10),
      height: parseInt(match[2], 10),
    }
  }
  return null
}

// Process gallery images, filtering duplicates and selecting best sizes
const galleryImages = computed(() => {
  if (!props.bubble.offlineMediaResources?.web_images) return []

  const imageGroups = new Map<string, string[]>()

  // Group images by their base filename
  const webImages = props.bubble.offlineMediaResources.web_images
  if (typeof webImages === 'string') return [] // Handle case where it's a string

  // Now we know it's a Record<string, string>
  Object.values(webImages).forEach((path: string) => {
    const baseFilename = getBaseFilename(path)
    const group = imageGroups.get(baseFilename) || []
    group.push(path)
    imageGroups.set(baseFilename, group)
  })

  // For each group, select the best image (prefer larger sizes but not too large)
  const selectedImages: MediaItem[] = []
  imageGroups.forEach((paths) => {
    // Sort by dimensions, preferring images close to 1200px width
    const sorted = paths.sort((a, b) => {
      const dimA = getDimensions(a)
      const dimB = getDimensions(b)
      if (!dimA || !dimB) return 0

      // Calculate how close each image is to target width (1200px)
      const targetWidth = 1200
      const diffA = Math.abs(dimA.width - targetWidth)
      const diffB = Math.abs(dimB.width - targetWidth)

      return diffA - diffB
    })

    // Use the best sized image
    const bestImage = sorted[0]
    if (bestImage) {
      selectedImages.push({
        type: 'image',
        src: dataStore + bestImage,
      })
    }
  })

  return selectedImages
})

const copyUrlToClipboard = async () => {
  if (props.bubble.url) {
    try {
      await navigator.clipboard.writeText(props.bubble.url)
    } catch (err) {
      logger.logError('Failed to copy URL:', err)
    }
  }
}

// Handle keyboard navigation
function handleKeydown(event: KeyboardEvent) {
  // Skip navigation if gallery or any modal is open
  if (isKeyboardNavigationDisabled()) return

  // Skip if event originated from input elements
  if (
    event.target instanceof HTMLInputElement ||
    event.target instanceof HTMLTextAreaElement ||
    event.target instanceof HTMLSelectElement
  ) {
    return
  }

  if (event.key === 'ArrowLeft') {
    const previousUuid = navigationStore.getPreviousItem(props.bubble.uuid)
    if (previousUuid) {
      router.push(`/bubble/${previousUuid}`)
    }
  } else if (event.key === 'ArrowRight') {
    const nextUuid = navigationStore.getNextItem(props.bubble.uuid)
    if (nextUuid) {
      router.push(`/bubble/${nextUuid}`)
    }
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
/* Scoped styles for WebPageDetailedView */
</style>

<!-- import pako from 'pako';

const decompressData = (compressedData) => {
  const decompressedData = pako.inflate(compressedData, { to: 'string' });
  return decompressedData;
}; -->
